"use client"

import { useLocation, useNavigate } from "react-router-dom"
import { useEffect, useState } from "react"
import {
  fetchFavoriteBook,
  getReviewsForBook,
  createReview,
  updateReview,
  deleteReview,
  fetchAverageRating,
  addFavorite,
  deleteFavorite,
  addOrUpdateRating,
  getFavorites,
  getRatings,
} from "../services/api.js"
import "./BookDetails.css"

function BookDetails() {
  const location = useLocation()
  const navigate = useNavigate()
  const { bookId, book: passedBook } = location.state || {}
  const [book, setBook] = useState(passedBook || null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [reviews, setReviews] = useState([])
  const [userReview, setUserReview] = useState("")
  const [isEditing, setIsEditing] = useState(false)
  const [averageRating, setAverageRating] = useState(null)
  const [ratingsCount, setRatingsCount] = useState(0)
  const [isFavorite, setIsFavorite] = useState(false)
  const [userRating, setUserRating] = useState(0)
  const [hoveredStarIndex, setHoveredStarIndex] = useState(-1)

  const userId = Number(localStorage.getItem("userId"))
  const username = localStorage.getItem("username")

  useEffect(() => {
    if (book) {
      setLoading(false) // already have book from state
    } else if (bookId) {
      fetchFavoriteBook(bookId)
        .then((data) => {
          setBook(data)
          setLoading(false)
        })
        .catch((err) => {
          setError(err.message)
          setLoading(false)
        })
    } else {
      setError("Missing book ID")
      setLoading(false)
    }

    if (bookId) {
      getReviewsForBook(bookId)
        .then((data) => setReviews(data))
        .catch((err) => console.error("Failed to fetch reviews:", err))

      // Fetch average rating and ratings count
      fetchAverageRating(bookId)
        .then(({ averageRating, ratingsCount }) => {
          setAverageRating(averageRating)
          setRatingsCount(ratingsCount)
        })
        .catch((err) => {
          console.error("Failed to fetch average rating:", err)
        })

      // Check if book is in user's favorites
      if (userId) {
        getFavorites(userId)
          .then((favorites) => {
            const isFav = favorites.some((fav) => fav.id === bookId)
            setIsFavorite(isFav)
          })
          .catch((err) => {
            console.error("Failed to fetch favorites:", err)
          })

        // Get user's rating for this book
        getRatings(userId)
          .then((ratings) => {
            const ratedBook = ratings.find((r) => r.id === bookId)
            if (ratedBook) {
              setUserRating(ratedBook.userRating)
            }
          })
          .catch((err) => {
            console.error("Failed to fetch user ratings:", err)
          })
      }
    }
  }, [bookId, book, userId])

  const handleReviewSubmit = async () => {
    if (!userId) {
      alert("You must be logged in to review a book.")
      return
    }

    try {
      if (isEditing) {
        await updateReview(userId, bookId, userReview)
      } else {
        await createReview(userId, bookId, userReview)
      }
      const updatedReviews = await getReviewsForBook(bookId)
      setReviews(updatedReviews)
      setIsEditing(false)
      setUserReview("")
    } catch (err) {
      console.error("Failed to save review:", err)
    }
  }

  const handleFavoriteToggle = async () => {
    if (!userId) {
      alert("You need to be logged in to favorite books.");
      return;
    }
  
    const optimisticFavorite = !isFavorite;
    setIsFavorite(optimisticFavorite); // ⬅️ Optimistic update first
  
    try {
      if (optimisticFavorite) {
        await addFavorite(userId, book);
      } else {
        await deleteFavorite(userId, bookId);
      }
    } catch (err) {
      console.error("Failed to update favorite status:", err);
      setIsFavorite(!optimisticFavorite); // ⬅️ Rollback if it fails
    }
  };
  

  const handleRating = async (index) => {
    if (!userId) {
      alert("You need to be logged in to rate books.");
      return;
    }
  
    const newRating = (index + 1) * 2; // 2, 4, 6, 8, 10
    const previousRating = userRating; // Save in case we need rollback
  
    setUserRating(newRating); // ⬅️ Optimistic update first
  
    try {
      await addOrUpdateRating(userId, book, newRating);
  
      // Optionally refresh average rating after
      const { averageRating, ratingsCount } = await fetchAverageRating(bookId);
      setAverageRating(averageRating);
      setRatingsCount(ratingsCount);
    } catch (err) {
      console.error("Failed to rate book:", err);
      setUserRating(previousRating); // ⬅️ Rollback if needed
    }
  };
  

  const handleStarHover = (index) => {
    setHoveredStarIndex(index)
  }

  const handleStarHoverEnd = () => {
    setHoveredStarIndex(-1)
  }

  const handleDeleteReview = async () => {
    try {
      await deleteReview(userId, bookId)
      const updatedReviews = await getReviewsForBook(bookId)
      setReviews(updatedReviews)
      setUserReview("")
      setIsEditing(false)
    } catch (err) {
      console.error("Failed to delete review:", err)
    }
  }

  const handleEditReview = (text) => {
    setUserReview(text)
    setIsEditing(true)
  }

  const handleGoBack = () => {
    navigate(-1)
  }

  if (loading) {
    return (
      <div className="book-details-page">
        <div className="loading-container">
          <div className="loader"></div>
          <p>Loading book details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="book-details-page">
        <div className="error-container">
          <h2>Oops!</h2>
          <p className="error-message">{error}</p>
          <button className="back-button" onClick={handleGoBack}>
            Return to previous page
          </button>
        </div>
      </div>
    )
  }

  if (!book) {
    return (
      <div className="book-details-page">
        <div className="error-container">
          <h2>Book Not Found</h2>
          <p className="error-message">We couldn't find the requested book.</p>
          <button className="back-button" onClick={handleGoBack}>
            Return to previous page
          </button>
        </div>
      </div>
    )
  }

  const currentUserReview = reviews.find((r) => r.username === username)
  const otherReviews = reviews.filter((r) => r.username !== username)

  return (
    <div className="book-details-page">
      <div className="book-details-container">
        <button className="back-button" onClick={handleGoBack}>
          ← Back
        </button>

        <div className="book-details-content">
          <div className="book-details-left">
            {book.thumbnail && (
              <div className="book-cover-container">
                <img src={book.thumbnail || "/placeholder.svg"} alt={book.title} className="book-cover1" />
              </div>
            )}

            

            <div className="book-ratings">
              <span className="rating-label">Average Rating:</span>
              <span className="rating-value">
              {averageRating && !isNaN(averageRating) ? Number(averageRating).toFixed(2) : "N/A"} 
              ({ratingsCount} ratings)
            </span>
            </div>

            {/* User interaction section */}
            <div className="user-book-actions">
              {/* Rating stars */}
              <div className="book-rating1" onMouseLeave={handleStarHoverEnd}>
                <div className="rating-label1">Your Rating:</div>
                <div className="stars-container1">
                  {[...Array(5)].map((_, index) => (
                    <button
                      key={index}
                      className={`star1 ${index < userRating / 2 ? "filled" : ""} ${index <= hoveredStarIndex ? "hovered" : ""}`}
                      onClick={() => handleRating(index)}
                      onMouseOver={() => handleStarHover(index)}
                    >
                      ★
                    </button>
                  ))}
                </div>
              </div>

              {/* Favorite button */}
              <button className={`favorite-button ${isFavorite ? "favorited" : ""}`} onClick={handleFavoriteToggle}>
                {isFavorite ? "❤️ Saved to Favorites" : "🤍 Add to Favorites"}
              </button>
            </div>
          </div>

          <div className="book-details-right">
            <div className="book-header">
              <h1 className="book-title1">{book.title}</h1>
              {book.subtitle && <h2 className="book-subtitle">{book.subtitle}</h2>}
              <p className="book-authors1">By {book.authors.join(", ")}</p>
            </div>

            {book.description && (
              <div className="book-section">
                <h3 className="section-title">Description</h3>
                <p className="book-description">{book.description}</p>
              </div>
            )}

            <div className="book-section">
              <h3 className="section-title">Details</h3>
              <div className="book-metadata">
                {book.publisher && (
                  <div className="metadata-item">
                    <span className="metadata-label">Publisher</span>
                    <span className="metadata-value">{book.publisher}</span>
                  </div>
                )}
                {book.publishedDate && (
                  <div className="metadata-item">
                    <span className="metadata-label">Published Date</span>
                    <span className="metadata-value">{book.publishedDate}</span>
                  </div>
                )}
                {book.pageCount && (
                  <div className="metadata-item">
                    <span className="metadata-label">Pages</span>
                    <span className="metadata-value">{book.pageCount}</span>
                  </div>
                )}
                {book.language && (
                  <div className="metadata-item">
                    <span className="metadata-label">Language</span>
                    <span className="metadata-value">{book.language.toUpperCase()}</span>
                  </div>
                )}
                {book.categories && book.categories.length > 0 && (
                  <div className="metadata-item">
                    <span className="metadata-label">Categories</span>
                    <div className="categories-container">
                      {book.categories.map((category, index) => (
                        <span key={index} className="category-tag">
                          {category}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="book-section">
              <h3 className="section-title">Reviews</h3>

              {!currentUserReview || isEditing ? (
                <div className="review-box">
                  <textarea
                    value={userReview}
                    onChange={(e) => setUserReview(e.target.value)}
                    placeholder="Write your review..."
                  />
                  <button className="publish-btn" onClick={handleReviewSubmit}>
                    {isEditing ? "Update" : "Publish"} Review
                  </button>
                </div>
              ) : null}

              {currentUserReview && !isEditing && (
                <div className="review-item">
                  <div className="review-header">
                    <strong>You</strong>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button className="edit-btn" onClick={() => handleEditReview(currentUserReview.content)}>
                        Edit
                      </button>
                      <button className="edit-btn" style={{ color: "red" }} onClick={handleDeleteReview}>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="review-content">{currentUserReview.content}</div>
                </div>
              )}

              {otherReviews.map((review, idx) => (
                <div key={idx} className="review-item">
                  <div className="review-header">
                    <strong>{review.username}</strong>
                  </div>
                  <div className="review-content">{review.content}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BookDetails
